<template>
  <div>
    <PlIssuanceStatusBadge :status="status" :flags="flags" />

    <template v-if="canEdit">
      <div class="mt-2">
        <div
          class="alert alert-subtle-warning p-3 d-inline-flex flex-column gap-2"
        >
          <PlIssuanceStatusEditor
            v-if="isEditing"
            :status="status"
            :flags="flags"
            @save="handleSave"
            @cancel="isEditing = false"
          />
          <button
            v-else
            class="btn btn-sm btn-outline-primary"
            type="button"
            @click="isEditing = true"
          >
            {{ $t("common.edit") }}
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { IssuanceProductionDataCountryMetadata } from "@/dashboard/production_strategy/issuance-reporting/useIssuanceProductionDataStore";
import { computed, ref, watch } from "vue";
import PlIssuanceStatusBadge from "./PlIssuanceStatusBadge.vue";
import PlIssuanceStatusEditor from "./PlIssuanceStatusEditor.vue";
import {
  getPlMetadata,
  type PlIssuanceFlags,
  type PlIssuanceStatus,
} from "./plStatusUtils";
import { mergeCountryMetadata } from "./utils/mergeCountryMetadata";

const props = defineProps<{
  countryMetadata?: IssuanceProductionDataCountryMetadata;
  canEdit?: boolean;
}>();

const emit = defineEmits<{
  save: [payload: { country_metadata: IssuanceProductionDataCountryMetadata }];
}>();

// Extract PL-specific data from country metadata
const plMetadata = computed(() => getPlMetadata(props.countryMetadata));
const status = computed(() => plMetadata.value?.pl_issuance_status ?? null);
const flags = computed(() => plMetadata.value?.pl_issuance_flags ?? null);

const isEditing = ref(false);

watch(status, () => {
  isEditing.value = false;
});

const handleSave = (payload: {
  pl_issuance_status: PlIssuanceStatus | null;
  pl_issuance_flags: PlIssuanceFlags;
}) => {
  // Merge PL updates with existing country metadata to preserve other country data
  const mergedMetadata = mergeCountryMetadata(props.countryMetadata, {
    pl_issuance_status: payload.pl_issuance_status,
    pl_issuance_flags: payload.pl_issuance_flags,
  });
  emit("save", { country_metadata: mergedMetadata });
  isEditing.value = false;
};
</script>
